import { useEffect, useState, useMemo, ReactElement, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { subject } from '@casl/ability'
import { Form, Space } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import cn from 'classnames'
import Select from 'components/Select/Select'
import { Input } from 'components/Input'
import { FloatFooter } from '../FloatFooter/FloatFooter'
import { UserArchivePopup } from 'pages/CRUDUser/UserArchivePopup'
import { Button, BUTTON_TYPES } from 'components/Button'
import { Tooltip } from 'components/Tooltip/Tooltip'
import {
  fetchCommunitiesWithHs,
  fetchDepartments,
  fetchHospitals,
  fetchTimeZonesList
} from 'pages/CRUDUser/api'
import { getUser } from 'redux/store/user/getters'
import {
  formatMoney,
  formatName,
  formatPhoneNumber,
  getLocalAccessToken,
  preventInitialToCorrectFormat,
  validateMessages
} from 'helper/common'
import {
  ACTIONS,
  ROLES,
  SUBJECTS,
  SubjectWithAttributes
} from 'features/Permission/constants'
import defineAbilityFor from 'features/Permission/ability'
import { fetchHealthSystemsList } from 'features/HealthSystems/api'
import { setLoading } from 'redux/store/common/slice'
import {
  emptyStateUser,
  ROLES_OPTIONS_ALL,
  ROLES_OPTIONS_CA,
  ROLES_OPTIONS_COMMONS_CS,
  ROLES_OPTIONS_COMMONS_VP,
  ROLES_OPTIONS_COMMUNITY,
  ROLES_OPTIONS_CS,
  ROLES_OPTIONS_HOSPITALS,
  ROLES_OPTIONS_HOSPITALS_FOR_HOSPITAL_ADMIN,
  ROLES_OPTIONS_VENDOR_CS,
  ROLES_OPTIONS_VICE
} from 'constants/common'
import { BTN_TXT, LABELS, PLACEHOLDERS, TOOLTIP } from 'constants/txt'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { ReactComponent as Delete24 } from 'assets/svg/Delete24.svg'
import { THealthSystem } from 'features/HealthSystems/types'
import { TListItem, TUserForm, TUserFormProps } from './forms.user.d'
import { TOption } from 'components/Select/types'
import { Avatar } from 'components/Avatar/Avatar'
import { API_HOST, API_URL } from 'services/Fetcher'
import axios, { AxiosResponse } from 'axios'
import { ReactComponent as DropPhotoIcon } from 'assets/svg/DropPhoto.svg'
import Typography from 'components/Typography/Typography'
import { Switch } from '../Switch'
import { getIsLoading } from '../../redux/store/common/getters'
import { ReactComponent as Information } from 'assets/svg/Information.svg'
import { notification } from '../Notification'
import usePopup from '../../hooks/usePopup'
import { Can } from 'features/Permission'
import { useParams } from 'react-router-dom'
import { TTimezonesItem } from '../../pages/CRUDUser/types'
import './forms.user.scss'

const UserAvatar = ({ url, name }: { url: string; name: string }) => {
  return (
    <>
      <div className="user-form_avatar_hover-cover">
        <DropPhotoIcon />
        <Typography.Button>Upload</Typography.Button>
      </div>
      <Avatar
        className="user-form_avatar-icon"
        name={url ? '' : name || ''}
        src={url || null}
        gap={5}
        style={{ height: '120px', width: '120px' }}
      />
    </>
  )
}

const getBase64 = (img, callback) => {
  const reader = new FileReader()
  reader.addEventListener('load', () => callback(reader.result))
  reader.readAsDataURL(img)
}

export const User = ({
  className,
  onSubmit,
  initialState,
  hsHosUser,
  hsUser,
  isProfile,
  setIsActive,
  isActive,
  form,
  fromCommunity,
  isPendingUserNeedsToReviewCategories
}: TUserFormProps): ReactElement => {
  const [departments, setDepartments] = useState<TListItem[]>(
    initialState?.departments || []
  )
  const [hospitalsList, setHospitalsList] = useState<TListItem[]>([])
  const [communitiesList, setCommunitiesList] = useState<TListItem[]>([])
  const [timezonesList, setTimezonesList] = useState<TTimezonesItem[]>([])
  const [error, setError] = useState({} as TUserForm)
  const [healthSystems, setHealthSystems] = useState<THealthSystem[]>([])
  const discardChangesPopup = usePopup()
  const inputRole = Form.useWatch('role', form)
  const healthSystem = Form.useWatch('health_system', form)
  const hospital = Form.useWatch('hospital', form)
  const hospitalsValue = Form.useWatch('hospital', form)
  const hospitalsField = Form.useWatch('hospitals', form)
  const communitiesFiled = Form.useWatch('communities', form)
  const hospitalOption = Form.useWatch(['hospitals', 0, 'hospital'], form)
  const communityOption = Form.useWatch(['communities', 0, 'community'], form)

  const selectedHospitals = Form.useWatch('hospitals', form)?.filter(
    (i) => i?.hospital
  )
  const selectedCommunities = Form.useWatch('communities', form)?.filter(
    (i) => i?.community
  )
  const phoneNumber = Form.useWatch('phone_number', form)
  const [role, setRole] = useState(inputRole || initialState?.role)
  const [hospitals, setHospitals] = useState([{}])
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const loading = useSelector(getIsLoading)
  const { id } = useParams()
  const [avatarPreview, setAvatarPreview] = useState(initialState?.avatar)
  const disableNonIdentityField = (disabled = false) =>
    !!isPendingUserNeedsToReviewCategories || disabled

  const setPredefinedHospital = useCallback(() => {
    if (
      role === ROLES.HOSPITAL_USER &&
      (id === 'new' || (initialState?.uuid && !initialState?.hospitals?.length))
    ) {
      form.setFieldsValue({
        hospitals: [undefined]
      })
    }
    if (user.role === ROLES.HOSPITAL_ADMIN && id === 'new') {
      fetchDepartments(
        user.community || '',
        user.health_system || '',
        user.hospital || ''
      ).then((data) => setDepartments(data?.data?.results))

      if (role === ROLES.HOSPITAL_USER) {
        form.setFieldsValue({
          hospitals: [
            {
              departments: undefined,
              hospital: user.hospital
            }
          ]
        })
      }
    }
  }, [
    role,
    id,
    initialState?.uuid,
    initialState?.hospitals?.length,
    user.role,
    user.community,
    user.health_system,
    user.hospital,
    form
  ])

  useEffect(() => {
    if (user.community || user.vendor || user.commons) {
      getSelectorsData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.community, user.vendor])

  useEffect(() => {
    form.setFieldsValue({
      hospitals: [
        {
          departments: undefined,
          hospital: hospitalsValue?.[hospitalsValue - 1]?.value
        }
      ]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalsValue])

  useEffect(() => {
    setRole(inputRole)
  }, [inputRole])

  useEffect(() => {
    initialState?.role && setRole(initialState?.role)
    if (initialState?.community_uuid && initialState?.health_system) {
      fetchHospitals(
        initialState?.community_uuid,
        initialState?.health_system
      ).then((data) => setHospitalsList(data?.data?.results))
      fetchDepartments(
        initialState?.community_uuid,
        initialState?.health_system
      ).then((data) => setDepartments(data?.data?.results))
    }
    isProfile ? form.resetFields(['hospitals']) : form.resetFields()

    setPredefinedHospital()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState])

  useEffect(() => {
    setPredefinedHospital()
    setPredefinedHealthSystemForHospitalUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, id])

  const getHospitalsDepartments = async (getHs?) => {
    const hs = form.getFieldValue(['health_system']) || getHs
    const hospital =
      form.getFieldValue('hospital') ||
      form.getFieldValue(['hospitals', 0, 'hospital'])
    if (user?.community && hs) {
      await fetchHospitals(user?.community, hs).then((data) => {
        setHospitalsList(data?.data?.results)
      })
      await fetchDepartments(user?.community, hs, hospital).then((data) =>
        setDepartments(data?.data?.results)
      )
    }
  }
  const setPredefinedHealthSystemForHospitalUser = useCallback(() => {
    if (
      id === 'new' &&
      role === ROLES.HOSPITAL_USER &&
      user.role === ROLES.COMMUNITY_PRESIDENT &&
      !hsUser
    ) {
      form.setFieldsValue({
        health_system: healthSystems[0].uuid
      })
      getHospitalsDepartments(healthSystems[0].uuid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, role, user.role, hsUser, form, healthSystems])

  const getCommunitisWithHs = useCallback(async () => {
    if (user?.commons) {
      await fetchCommunitiesWithHs().then((data) => {
        setCommunitiesList(data?.data?.results)
      })
    }
  }, [user?.commons])

  const getTimezonesList = async () => {
    await fetchTimeZonesList().then((data) => {
      setTimezonesList(data?.data)
    })
  }

  const getSelectorsData = async () => {
    if (!loading) {
      dispatch(setLoading(true))
    }
    try {
      if (user.community) {
        fetchHealthSystemsList(user.community).then((resp) => {
          if (!resp.data?.results) {
            return
          }

          setHealthSystems(resp.data.results)
          setPredefinedHealthSystemForHospitalUser()
        })
      }
      if (initialState?.health_system) {
        getHospitalsDepartments(initialState?.health_system)
      }
      if (
        initialState?.communities ||
        role === ROLES.COMMONS_CLINICAL_COORDINATOR
      ) {
        getCommunitisWithHs()
      }
      if (isProfile) {
        getTimezonesList()
      }
    } finally {
      dispatch(setLoading(false))
    }
  }

  const returnSelectOptions = (arr: TListItem[]): TOption[] => {
    if (arr?.length) {
      return arr?.map((item) => ({ label: item?.name, value: item?.uuid }))
    }
    return []
  }
  const returnSelectHospitalsOptions = (arr: TListItem[], index): TOption[] => {
    if (arr?.length) {
      return (
        arr
          ?.filter(
            (item) =>
              !(
                selectedHospitals?.find((i) => i?.hospital === item.uuid) &&
                selectedHospitals?.[index]?.hospital !== item.uuid
              )
          )
          ?.map((item) => ({ label: item?.name, value: item?.uuid })) ?? []
      )
    }
    return []
  }
  const returnSelectCommunitiesOptions = (
    arr: TListItem[],
    index
  ): TOption[] => {
    if (arr?.length) {
      return (
        arr
          ?.filter(
            (item) =>
              !(
                selectedCommunities?.find((i) => i?.community === item.uuid) &&
                selectedCommunities?.[index]?.community !== item.uuid
              )
          )
          ?.map((item) => ({ label: item?.name, value: item?.uuid })) ?? []
      )
    }
    return []
  }

  const returnSelectOptionsDepartments = (arr, index): TOption[] => {
    if (arr?.length && selectedHospitals?.length) {
      const deps =
        arr?.find(
          (i) => i?.uuid === (selectedHospitals?.[index]?.hospital || '')
        )?.departments || []
      return deps?.map((item) => ({ label: item?.name, value: item?.uuid }))
    }
    return []
  }
  const returnSelectHsOptionsFromCommunity = (arr, index): TOption[] => {
    if (arr?.length && selectedCommunities?.length) {
      const hs =
        arr?.find(
          (i) => i?.uuid === (selectedCommunities?.[index]?.community || '')
        )?.health_systems || []
      return hs?.map((item) => ({ label: item?.name, value: item?.uuid }))
    }
    return []
  }

  const submitCancellation = () => {
    isProfile && form.getFieldValue(['contract_categories'])
      ? form.resetFields(['phone_number', 'first_name', 'last_name', 'avatar'])
      : form.resetFields()
    setError({} as TUserForm)
    discardChangesPopup.actions.close()
    setAvatarPreview(initialState?.avatar)

    setIsActive(false)
  }
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState<boolean>(false)

  const onSubmitForm = (values) => {
    setIsActive(false)
    dispatch(setLoading(true))
    if (values?.phone_number) {
      values.phone_number = !values?.phone_number.toString().includes('+1')
        ? '+1' + values.phone_number
        : values.phone_number
    } else delete values?.phone_number
    onSubmit(values)
      .then(() => {
        setError({} as TUserForm)
        if (isProfile) {
          dispatch(setLoading(false))
        } else {
          if (role.includes('vendor') || role.includes('commons')) {
            dispatch(setLoading(false))
          }
        }
      })
      .catch((e) => {
        setIsActive(true)
        setError(e.data)
        dispatch(setLoading(false))
      })
  }

  const healthSystemSelect = () => (
    <Select
      options={returnSelectOptions(healthSystems)}
      propsItem={{
        label: LABELS.HEALTH_SYSTEM,
        name: 'health_system',
        rules: [{ required: true }]
      }}
      propsSelect={{
        placeholder: PLACEHOLDERS.PLEASE_SELECT,
        required: true,
        disabled: disableNonIdentityField(
          !!hsHosUser ||
            !!hsUser ||
            isProfile ||
            user.uuid === initialState?.uuid ||
            defineAbilityFor(role, user, initialState?.uuid).can(
              ACTIONS.EDIT,
              SUBJECTS.FORM_ITEM
            ) ||
            (!isProfile &&
              defineAbilityFor(role, user, initialState?.uuid).can(
                ACTIONS.VIEW,
                SUBJECTS.FORM_ITEM
              )) ||
            defineAbilityFor(role, user, initialState?.uuid).can(
              ACTIONS.DISABLED,
              SUBJECTS.FORM_HEALTH_SYSTEM_INPUT
            )
        ),
        loading: !healthSystems?.length && !isProfile,
        onChange: () => {
          getHospitalsDepartments()
          form.resetFields(['hospitals'])
          if (role === ROLES.HOSPITAL_ADMIN) {
            form.setFieldsValue({ hospital: '', hospitals: '' })
          }
          form.resetFields(['departments'])
        }
      }}
    />
  )

  const hospitalsAreNotEditable = useMemo(() => {
    return defineAbilityFor(role, user, initialState?.uuid).can(
      ACTIONS.DISABLED,
      SUBJECTS.FORM_HOSPITAL_INPUT
    )
  }, [role, user, initialState?.uuid])

  // disable delete hospital for HA role if this hospital is not assigned to the HA/ this hospital is the only one or on create mode
  const disableHospitalsDeletion = useCallback(
    (name) => {
      return (
        hospitalsAreNotEditable &&
        hospitalsField?.length > 1 &&
        (id === 'new' ||
          (id !== 'new' && user.hospital !== hospitalsField?.[name]?.hospital))
      )
    },
    [hospitalsAreNotEditable, id, user.hospital, hospitalsField]
  )

  const disableDepartmentsForHARole = useCallback(
    (name) => {
      if (user.role === ROLES.HOSPITAL_ADMIN) {
        return hospitalsField?.[name]?.hospital !== user.hospital
      }
      return false
    },
    [user.role, user.hospital, hospitalsField]
  )

  const returnHospitals = (key, name, restField, remove, numberInQueue) => {
    const disableHandler = () => {
      if (isPendingUserNeedsToReviewCategories) {
        return true
      }
      if (disableHospitalsDeletion(name)) {
        return true
      }
      if (hsHosUser) {
        return hospitals.length <= 1
      } else {
        return false
      }
    }
    return (
      <>
        <Space
          key={key}
          className="user-form_input-wide hospitals-wrapper"
          direction="vertical"
        >
          <div
            className={cn('user-form_hospitals_header', {
              disabled: disableHandler()
            })}
          >
            <div className="user-form_title">Hospital {name + 1}</div>
            <Tooltip
              title={TOOLTIP.AT_LEAST_ONE_HOSPITAL_SHOULD_BE_ASSIGNED}
              overlayInnerStyle={{
                display:
                  disableHandler() &&
                  defineAbilityFor(role, user, initialState?.uuid).can(
                    ACTIONS.EDIT,
                    SUBJECTS.FORM_HOSPITAL_LIST
                  )
                    ? 'block'
                    : 'none'
              }}
              placement="topRight"
            >
              {defineAbilityFor(role, user, initialState?.uuid).can(
                ACTIONS.EDIT,
                SUBJECTS.FORM_HOSPITAL_LIST
              ) && (
                <Button.Icon
                  className="user-form_delete-btn"
                  type={BUTTON_TYPES.LINK}
                  disabled={
                    role === ROLES.HOSPITAL_USER && hospitalsField?.length === 1
                  }
                  icon={<Delete24 />}
                  onClick={() => {
                    if (disableHandler()) return
                    remove(name)
                  }}
                  danger
                />
              )}
            </Tooltip>
          </div>
          <div className="user-form_hospitals_selects">
            <Select
              options={returnSelectHospitalsOptions(
                hospitalsList,
                numberInQueue
              )}
              propsItem={{
                ...restField,
                label: LABELS.HOSPITAL,
                name: [name, 'hospital'],
                rules: [
                  {
                    required:
                      !!hsHosUser ||
                      (role === ROLES.HOSPITAL_USER && name === 0)
                  }
                ]
              }}
              propsSelect={{
                disabled: disableNonIdentityField(
                  hospitalsAreNotEditable ||
                    isProfile ||
                    !defineAbilityFor(role, user, initialState?.uuid).can(
                      ACTIONS.EDIT,
                      SUBJECTS.FORM_HOSPITAL_LIST
                    )
                ),
                loading: !hospitalsList?.length && !isProfile,
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                onChange: (value) => {
                  form.setFieldsValue({
                    hospitals: hospitalsField.map((i, idx) =>
                      idx === numberInQueue
                        ? {
                            hospital: value,
                            departments: [],
                            contract_categories: []
                          }
                        : i
                    )
                  })
                }
              }}
            />
            <Select.Multi
              options={returnSelectOptionsDepartments(
                hospitalsList,
                numberInQueue
              )}
              propsItem={{
                ...restField,
                label: LABELS.DEPARTMENTS,
                name: [name, 'departments']
              }}
              propsSelect={{
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                disabled: disableNonIdentityField(
                  disableDepartmentsForHARole(name) ||
                    isProfile ||
                    !hospitalOption ||
                    selectedHospitals?.length < numberInQueue + 1 ||
                    !defineAbilityFor(role, user, initialState?.uuid).can(
                      ACTIONS.EDIT,
                      SUBJECTS.FORM_HOSPITAL_LIST
                    )
                ),
                loading: !departments?.length && !isProfile
              }}
            />
          </div>
        </Space>
      </>
    )
  }
  const returnCommunitiesWithHs = (
    key,
    name,
    restField,
    remove,
    numberInQueue
  ) => (
    <Space
      key={key}
      className="user-form_input-wide hospitals-wrapper"
      direction="vertical"
    >
      <div className={cn('user-form_hospitals_header')}>
        <div className="user-form_title">Community {name + 1}</div>
        {defineAbilityFor(role, user, initialState?.uuid).can(
          ACTIONS.EDIT,
          SUBJECTS.FORM_COMMUNITIES_WITH_HS_LIST
        ) &&
          !isPendingUserNeedsToReviewCategories && (
            <Button.Icon
              className="user-form_delete-btn"
              type={BUTTON_TYPES.LINK}
              icon={<Delete24 />}
              onClick={() => {
                remove(name)
              }}
              danger
            />
          )}
      </div>
      <div className="user-form_hospitals_selects">
        <Select
          options={returnSelectCommunitiesOptions(
            communitiesList,
            numberInQueue
          )}
          propsItem={{
            ...restField,
            label: LABELS.COMMUNITY,
            name: [name, 'community'],
            rules: [{ required: !!hsHosUser }],
            help: error?.communities
              ? error?.communities?.[name]?.community
              : undefined,
            validateStatus: error?.communities?.[name]?.community
              ? 'error'
              : undefined
          }}
          propsSelect={{
            disabled: disableNonIdentityField(
              isProfile ||
                !defineAbilityFor(role, user, initialState?.uuid).can(
                  ACTIONS.EDIT,
                  SUBJECTS.FORM_COMMUNITIES_WITH_HS_LIST
                )
            ),
            loading: !communitiesList?.length && !isProfile,
            placeholder: PLACEHOLDERS.PLEASE_SELECT,
            onChange: (value) => {
              form.setFieldsValue({
                communities: communitiesFiled.map((i, idx) =>
                  idx === numberInQueue
                    ? {
                        community: value,
                        health_systems: []
                      }
                    : i
                )
              })
            }
          }}
        />
        <Select.Multi
          options={returnSelectHsOptionsFromCommunity(
            communitiesList,
            numberInQueue
          )}
          propsItem={{
            ...restField,
            label: LABELS.HEALTH_SYSTEMS,
            name: [name, 'health_systems'],
            help: error?.communities
              ? error?.communities?.[name]?.health_systems
              : undefined,
            validateStatus: !!error?.communities?.[name]?.health_systems
              ? 'error'
              : undefined
          }}
          propsSelect={{
            placeholder: PLACEHOLDERS.PLEASE_SELECT,
            disabled: disableNonIdentityField(
              isProfile ||
                !communityOption ||
                selectedCommunities?.length < numberInQueue + 1 ||
                !defineAbilityFor(role, user, initialState?.uuid).can(
                  ACTIONS.EDIT,
                  SUBJECTS.FORM_COMMUNITIES_WITH_HS_LIST
                )
            ),
            loading: !communityOption?.length && !isProfile
          }}
        />
      </div>
    </Space>
  )

  const hospitalsInitialValues = useMemo(() => {
    return {
      hospitals:
        initialState?.hospitals?.map((item) => {
          return {
            hospital: item?.hospital,
            contract_categories: item?.contract_categories,
            departments: item?.departments
          }
        }) ?? []
    }
  }, [initialState])

  const communitiesInitialValues = useMemo(() => {
    return {
      communities:
        initialState?.communities?.map((item) => {
          return {
            community: item.community,
            health_systems: item?.health_systems
          }
        }) ?? []
    }
  }, [initialState])

  const returnListOfAvailableRoles = useMemo(() => {
    if (hsHosUser) {
      if (user.role === ROLES.HOSPITAL_ADMIN) {
        return ROLES_OPTIONS_HOSPITALS_FOR_HOSPITAL_ADMIN
      }
      return ROLES_OPTIONS_HOSPITALS
    }
    if (
      !isProfile &&
      defineAbilityFor(role, user).can(ACTIONS.EDIT, SUBJECTS.FORM_ITEM)
    ) {
      return ROLES_OPTIONS_ALL
    }

    switch (user.role) {
      case 'community_president':
        return ROLES_OPTIONS_COMMUNITY
      case 'community_vice_president':
        return ROLES_OPTIONS_VICE
      case 'vendor_contract_admin':
        return ROLES_OPTIONS_CA
      case 'vendor_contract_steward':
        return ROLES_OPTIONS_VENDOR_CS
      case 'commons_vice_president':
        return ROLES_OPTIONS_COMMONS_VP
      case 'commons_contract_steward':
        return ROLES_OPTIONS_COMMONS_CS
      default:
        return ROLES_OPTIONS_CS
    }
  }, [hsHosUser, isProfile, role, user])

  const returnListOfAvailableTZ = useMemo(() => {
    return timezonesList.map((zone) => ({
      label: zone.value,
      value: zone.id
    }))
  }, [timezonesList])

  const firstName = Form.useWatch('first_name', form)
  const lastName = Form.useWatch('last_name', form)
  const [name, setName] = useState(
    `${firstName?.charAt(0)}${lastName?.charAt(0)}`.toUpperCase()
  )

  useEffect(() => {
    if (role === ROLES.COMMONS_CLINICAL_COORDINATOR) {
      getCommunitisWithHs()
    }
  }, [getCommunitisWithHs, role])

  const handleChange = (info) => {
    if (info?.file?.status === 'uploading') {
      setLoading(true)
      return
    }
    if (info?.file?.status === 'done') {
      getBase64(info.file.originFileObj, () => {
        setLoading(false)
      })
    }
  }
  useEffect(() => {
    setName(
      `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase()
    )
  }, [firstName, lastName])

  const canEditAdditionalInfo = useMemo(() => {
    return (
      isProfile &&
      defineAbilityFor(role, user, initialState?.uuid).can(
        ACTIONS.CRUD,
        SUBJECTS.ADDITIONAL_INFO_PROFILE
      )
    )
  }, [isProfile, role, user, initialState])

  const avatarImageRequest = async (options) => {
    const { onError, file } = options
    const fmData = new FormData()
    const token = getLocalAccessToken()
    fmData.append('file', file)
    dispatch(setLoading(true))
    try {
      const res = await axios.post(
        `${API_HOST}${API_URL}files/profile_image/`,
        fmData,
        { headers: { authorization: `Bearer ${token}` } }
      )
      if (res) {
        setIsActive(true)
        setAvatarPreview(res?.data?.file)
        form?.setFieldsValue({ avatar: res?.data?.uuid })
      }
    } catch (e) {
      const err = e as AxiosResponse
      const error = new Error('Some error')
      console.error('Error: ', err, error)
      onError({ err })
      const message = err?.data.file.file
      notification.error({ message })
      form?.setFieldsValue({ avatar: initialState?.avatar })
    } finally {
      dispatch(setLoading(false))
    }
  }

  const imageUrl = Form.useWatch('avatar', form)
  const formItemAbility = defineAbilityFor(role, user, initialState?.uuid)

  const isCommonInfoInputDisabled = useMemo(() => {
    return id === 'new'
      ? false
      : formItemAbility.can(ACTIONS.EDIT, SUBJECTS.FORM_ITEM) ||
          (!isProfile && formItemAbility.can(ACTIONS.VIEW, SUBJECTS.FORM_ITEM))
  }, [id, formItemAbility, isProfile])

  return (
    <CustomForm<TUserForm>
      validateMessages={validateMessages}
      form={form}
      className={cn('user-form_container', className)}
      layout="vertical"
      onFinish={onSubmitForm}
      initialValues={
        preventInitialToCorrectFormat(initialState) || emptyStateUser
      }
      onFieldsChange={() => setIsActive(true)}
      onValuesChange={(_, all) => {
        !!hsHosUser && all.hospitals && setHospitals(all.hospitals)
        all?.phone_number?.toString().length !== 13
          ? setIsPhoneNumberValid(true)
          : setIsPhoneNumberValid(false)
      }}
    >
      <div className="user-form_avatar">
        {!fromCommunity && (
          <Form.Item name="avatar">
            {defineAbilityFor(role, user, initialState?.uuid).can(
              ACTIONS.UPLOAD,
              SUBJECTS.AVATAR
            ) && !isPendingUserNeedsToReviewCategories ? (
              <Input.File
                imageUrl={imageUrl || ''}
                displayedBlock={
                  <UserAvatar url={avatarPreview || imageUrl} name={name} />
                }
                onChange={handleChange}
                customRequest={avatarImageRequest}
                capture={false}
              />
            ) : (
              <Avatar
                className="user-form_avatar-icon"
                name={avatarPreview || imageUrl ? '' : name || ''}
                src={
                  !!avatarPreview || imageUrl ? avatarPreview || imageUrl : null
                }
              />
            )}
          </Form.Item>
        )}
      </div>
      <div className="user-form">
        <Input
          propsItem={{
            label: LABELS.FIRST_NAME,
            name: 'first_name',
            help: error?.first_name ? error?.first_name[0] : undefined,
            validateStatus: error?.first_name ? 'error' : undefined,
            rules: [
              {
                pattern: /^[-\w\s]+$/,
                max: 40,
                required: true
              }
            ],
            normalize: formatName
          }}
          propsInput={{
            disabled: isCommonInfoInputDisabled,
            showCount: true,
            maxLength: 40
          }}
        />
        <Input
          propsItem={{
            label: LABELS.LAST_NAME,
            name: 'last_name',
            help: error?.last_name ? error?.last_name[0] : undefined,
            validateStatus: error?.last_name ? 'error' : undefined,
            rules: [
              {
                pattern: /^[-\w\s]+$/,
                max: 40,
                required: true
              }
            ]
          }}
          propsInput={{
            disabled: isCommonInfoInputDisabled,
            showCount: true,
            maxLength: 40
          }}
        />
        <Input
          propsItem={{
            label: LABELS.EMAIL,
            name: 'email',
            help: error?.email ? error?.email[0] : undefined,
            validateStatus: error?.email ? 'error' : undefined,
            validateTrigger: 'onBlur',
            rules: [
              {
                transform: (value) => value.trim(),
                required: true,
                type: 'email',
                validateTrigger: 'onSubmit'
              }
            ]
          }}
          propsInput={{
            placeholder: PLACEHOLDERS.EMAIL,
            disabled:
              isProfile ||
              user.uuid === initialState?.uuid ||
              isCommonInfoInputDisabled
          }}
        />
        {id !== 'new' && (
          <div className="column">
            <Input.Phone
              propsItem={{
                label: LABELS.PHONE_NUMBER,
                name: 'phone_number',
                help: error?.phone_number ? error?.phone_number[0] : undefined,
                validateStatus: error?.phone_number ? 'error' : undefined,
                rules: [
                  {
                    required: id === user?.uuid,
                    pattern: /^[0-9]{10}$/,
                    validateTrigger: isPhoneNumberValid
                      ? ['onSubmit']
                      : ['onBlur']
                  }
                ]
              }}
              propsInputNumber={{
                disabled: true,
                prefix: phoneNumber ? '+1' : '',
                controls: false,
                formatter: formatPhoneNumber
              }}
            />
            {defineAbilityFor(role, user, initialState?.uuid).can(
              ACTIONS.EDIT,
              SUBJECTS.HIDE_PHONE_NUMBER
            ) && (
              <div className="user-form__switch row justify-end align-center mt-8 gap-12">
                <Form.Item
                  name="hide_phone_number_for_vendor"
                  valuePropName="checked"
                >
                  <Switch disabled={!!isPendingUserNeedsToReviewCategories} />
                </Form.Item>
                <Typography.Body1>Hide for Vendors</Typography.Body1>
              </div>
            )}
          </div>
        )}
        {isProfile &&
          defineAbilityFor(role, user).can(
            ACTIONS.VIEW,
            subject(SUBJECTS.FORM_COMMUNITY_INPUT, {
              ...user
            } as unknown as SubjectWithAttributes)
          ) && (
            <div className="user-form_input-wide">
              <Input
                propsItem={{
                  label: LABELS.COMMUNITY_NAME,
                  name: 'community',
                  help: error?.community ? error?.community[0] : undefined,
                  validateStatus: error?.community ? 'error' : undefined,
                  rules: [
                    {
                      max: 100,
                      required: true
                    }
                  ]
                }}
                propsInput={{
                  disabled: disableNonIdentityField(isProfile),
                  showCount: true,
                  maxLength: 100
                }}
              />
            </div>
          )}
        {defineAbilityFor(role, user).can(
          ACTIONS.VIEW,
          SUBJECTS.ADDITIONAL_INFO_PROFILE
        ) &&
          id !== 'new' && (
            <>
              <Input
                propsItem={{
                  label: LABELS.TITLE,
                  name: 'title',
                  help: error?.title ? error?.title[0] : undefined,
                  validateStatus: error?.title ? 'error' : undefined,
                  rules: [
                    {
                      max: 50,
                      required: false
                    }
                  ]
                }}
                propsInput={{
                  disabled: disableNonIdentityField(!canEditAdditionalInfo),
                  showCount: true,
                  maxLength: 50
                }}
              />
              <div className="user-form_input-wide">
                <Input
                  propsItem={{
                    label: LABELS.VENDOR_PREFERENCES,
                    name: 'vendor_preferences',
                    help: error?.vendor_preferences
                      ? error?.vendor_preferences[0]
                      : undefined,
                    validateStatus: error?.vendor_preferences
                      ? 'error'
                      : undefined,
                    rules: [
                      {
                        max: 100,
                        required: false
                      }
                    ]
                  }}
                  propsInput={{
                    disabled: disableNonIdentityField(!canEditAdditionalInfo),
                    showCount: true,
                    maxLength: 100
                  }}
                />
              </div>
            </>
          )}
        <Select
          options={isProfile ? ROLES_OPTIONS_ALL : returnListOfAvailableRoles}
          propsItem={{
            label: LABELS.ROLE,
            name: 'role',
            help: error?.role ? error?.role[0] : undefined,
            validateStatus: error?.role ? 'error' : undefined,
            rules: [
              {
                required: true
              }
            ]
          }}
          propsSelect={{
            placeholder: PLACEHOLDERS.PLEASE_SELECT,
            onChange: () =>
              form.resetFields([
                'contract_stewards',
                'contract_categories',
                'departments',
                'target_amount',
                'hospitals',
                'hospital'
              ]),
            disabled: disableNonIdentityField(id !== 'new')
          }}
        />
        {(!!hsHosUser || !!hsUser) && (
          <div
            className={cn('user-form_health-system', {
              'user-form_input-wide': id === 'new'
            })}
          >
            {healthSystemSelect()}
          </div>
        )}
        {!hsHosUser &&
          !hsUser &&
          defineAbilityFor(role, user).can(
            ACTIONS.VIEW,
            SUBJECTS.FORM_HEALTH_SYSTEM_INPUT
          ) && (
            <div
              className={cn('user-form_health-system', {
                'user-form_input-wide': id === 'new'
              })}
            >
              {healthSystemSelect()}
            </div>
          )}
        {defineAbilityFor(role, user).can(
          ACTIONS.VIEW,
          SUBJECTS.FORM_HOSPITAL_INPUT
        ) && (
          <Select
            options={returnSelectOptions(hospitalsList)}
            className="user-form_hospital"
            propsItem={{
              label: LABELS.HOSPITAL,
              name: 'hospital',
              rules: [
                {
                  required: !!healthSystem,
                  validateTrigger: 'onSubmit'
                }
              ]
            }}
            propsSelect={{
              onChange: () => {
                getHospitalsDepartments()
                form.resetFields(['departments'])
                form.resetFields(['contract_categories'])
              },
              placeholder: PLACEHOLDERS.PLEASE_SELECT,
              disabled: disableNonIdentityField(
                hospitalsAreNotEditable ||
                  isProfile ||
                  !healthSystem ||
                  defineAbilityFor(role, user).can(
                    ACTIONS.EDIT,
                    SUBJECTS.FORM_ITEM
                  )
              ),
              loading: !hospitalsList?.length && (!isProfile || healthSystem)
            }}
          />
        )}
        {defineAbilityFor(role, user).can(
          ACTIONS.VIEW,
          SUBJECTS.FORM_DEPARTMENTS_INPUT
        ) && (
          <Select.Multi
            options={returnSelectOptions(departments)}
            propsItem={{
              label: LABELS.DEPARTMENTS,
              name: 'departments'
            }}
            propsSelect={{
              placeholder: PLACEHOLDERS.PLEASE_SELECT,
              disabled: disableNonIdentityField(
                isProfile ||
                  !hospital ||
                  defineAbilityFor(role, user).can(
                    ACTIONS.EDIT,
                    SUBJECTS.FORM_ITEM
                  )
              ),
              loading: !departments?.length && (!isProfile || healthSystem)
            }}
          />
        )}
        {defineAbilityFor(role, user, initialState?.uuid).can(
          ACTIONS.VIEW,
          SUBJECTS.FORM_HOSPITAL_LIST
        ) && (
          <Form.List
            name="hospitals"
            initialValue={hospitalsInitialValues.hospitals}
          >
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, id) =>
                  returnHospitals(key, name, restField, remove, id)
                )}
                {defineAbilityFor(role, user).can(
                  ACTIONS.EDIT,
                  SUBJECTS.FORM_ITEM
                ) ||
                  (!hospitalsAreNotEditable && (
                    <Button
                      type={BUTTON_TYPES.GHOST}
                      icon={<Plus16 />}
                      className="user-form_add-hospital"
                      onClick={() => add()}
                      disabled={disableNonIdentityField(
                        !healthSystem ||
                          selectedHospitals?.length !== hospitalsField?.length
                      )}
                      upperCase
                    >
                      {BTN_TXT.ADD_HOSPITAL}
                    </Button>
                  ))}
              </>
            )}
          </Form.List>
        )}
        {defineAbilityFor(role, user, initialState?.uuid).can(
          ACTIONS.VIEW,
          SUBJECTS.FORM_COMMUNITIES_WITH_HS_LIST
        ) && (
          <Form.List
            name="communities"
            initialValue={communitiesInitialValues.communities}
          >
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, id) =>
                  returnCommunitiesWithHs(key, name, restField, remove, id)
                )}
                {defineAbilityFor(role, user).can(
                  ACTIONS.EDIT,
                  SUBJECTS.FORM_COMMUNITIES_WITH_HS_LIST
                ) && (
                  <Button
                    type={BUTTON_TYPES.GHOST}
                    icon={<Plus16 />}
                    className="user-form_add-hospital"
                    onClick={() => add()}
                    disabled={disableNonIdentityField(
                      selectedCommunities?.length !== communitiesFiled?.length
                    )}
                    upperCase
                  >
                    {BTN_TXT.ADD_COMMUNITY}
                  </Button>
                )}
              </>
            )}
          </Form.List>
        )}
        {defineAbilityFor(role, user).can(
          ACTIONS.VIEW,
          SUBJECTS.FORM_SAVINGS_GOAL
        ) && (
          <Input.Number
            propsItem={{
              label: (
                <div className="user-form_goal_label">
                  <div>Annual savings goal, $</div>
                  <Tooltip
                    title={
                      <div>
                        Annual savings are collected for the period from Jan 1
                        till Dec 31. <br />
                        <br />
                        Current goal does not affect on previous years results.
                      </div>
                    }
                    placement="topLeft"
                  >
                    <Information />
                  </Tooltip>
                </div>
              ),
              name: 'target_amount',
              help: error?.target_amount ? error?.target_amount[0] : undefined,
              validateStatus: error?.target_amount ? 'error' : undefined,
              rules: [
                {
                  pattern: /^(\d*\.)?\d+$/
                }
              ]
            }}
            propsInputNumber={{
              disabled: disableNonIdentityField(
                defineAbilityFor(role, user, initialState?.uuid).can(
                  ACTIONS.EDIT,
                  SUBJECTS.FORM_SAVINGS_GOAL
                ) ||
                  (!isProfile &&
                    defineAbilityFor(role, user, initialState?.uuid).can(
                      ACTIONS.EDIT,
                      SUBJECTS.FORM_ITEM
                    ))
              ),
              controls: false,
              defaultValue: 0,
              formatter: formatMoney
            }}
          />
        )}
        {isProfile && !user.gpo && (
          <div className="user-form">
            <Select
              options={returnListOfAvailableTZ}
              propsItem={{
                label: LABELS.TIME_ZONE,
                name: 'timezone_id',
                help: error?.timezone_id ? error?.timezone_id[0] : undefined,
                validateStatus: error?.timezone_id ? 'error' : undefined,
                rules: [
                  {
                    required: true
                  }
                ]
              }}
              propsSelect={{
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                disabled: disableNonIdentityField(
                  !returnListOfAvailableTZ.length
                )
              }}
            />
          </div>
        )}
      </div>
      <div className="user-form_buttons">
        <Can I={ACTIONS.ARCHIVE} a={SUBJECTS.USER}>
          {!isProfile && initialState?.uuid && (
            <UserArchivePopup<TUserForm> selected={initialState} />
          )}
        </Can>
        <FloatFooter
          onCancel={submitCancellation}
          isActive={isActive}
          saveBtnText={
            hsHosUser || hsUser
              ? BTN_TXT.CREATE_AND_ADD_TO_LIST
              : !isProfile && !initialState?.uuid
              ? BTN_TXT.CREATE
              : BTN_TXT.SAVE
          }
          htmlType="submit"
        />
      </div>
    </CustomForm>
  )
}
